'use client';

import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { useMoonpayConfig } from '@/services/query';
import { MoonpayHeadlessDeposit } from './MoonpayHeadlessDeposit';
import { MoonpayHostedDeposit } from './MoonpayHostedDeposit';
import { DepositHeader } from './subcomponents/deposit';

import {
  fiatForCountry,
  isHeadlessCountry,
  MOONPAY_HEADLESS_COUNTRIES,
  MOONPAY_HOSTED_COUNTRIES,
} from '@/constants';

/**
 * MoonPay deposit — country-first router. The selected country decides the path
 * ONCE, with no runtime fallback:
 *   headless (16 LatAm markets) → our own KYC screens (MoonpayHeadlessDeposit)
 *   everything else             → MoonPay's signed widget (MoonpayHostedDeposit)
 */

interface MoonpayDepositPanelProps {
  onBack: () => void;
  onClose: () => void;
  balance?: number;
  /** Optional ISO alpha-2 to preselect (e.g. from the deposit modal). */
  defaultCountry?: string;
}

const selectCls =
  'w-full rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3.5 text-sm font-medium text-[var(--text-primary)] outline-none transition-colors focus:border-[var(--primary)]';

export const MoonpayDepositPanel = ({
  onBack,
  onClose,
  balance = 0,
  defaultCountry,
}: MoonpayDepositPanelProps) => {
  const { t } = useT();
  const { data: config } = useMoonpayConfig();

  const initial = useMemo(() => {
    const cc = String(defaultCountry || '').toUpperCase();
    return cc &&
      [...MOONPAY_HEADLESS_COUNTRIES, ...MOONPAY_HOSTED_COUNTRIES].some((c) => c.a2 === cc)
      ? cc
      : 'MX';
  }, [defaultCountry]);

  const [country, setCountry] = useState<string>(initial);
  const [routed, setRouted] = useState(false);

  const headless = isHeadlessCountry(country, config?.headlessCountries);
  const fiat = fiatForCountry(country);

  if (routed) {
    const common = { country, fiat, balance, onClose, onBack: () => setRouted(false) };
    return headless ? <MoonpayHeadlessDeposit {...common} /> : <MoonpayHostedDeposit {...common} />;
  }

  return (
    <Box className="flex min-h-[520px] flex-col">
      <DepositHeader
        balance={balance}
        onClose={onClose}
        onBack={onBack}
        title={t('moonpay.title', { defaultValue: 'Deposit with MoonPay' })}
      />

      <Box className="flex flex-1 flex-col gap-4">
        <Box>
          <Typography className="text-base font-bold text-[var(--text-primary)]">
            {t('moonpay.countryTitle', { defaultValue: 'Where do you live?' })}
          </Typography>
          <Typography className="mt-1 text-sm text-[var(--text-muted)]">
            {t('moonpay.countrySubtitle', {
              defaultValue: 'This sets your local currency and how we verify you.',
            })}
          </Typography>
        </Box>

        <select className={selectCls} value={country} onChange={(e) => setCountry(e.target.value)}>
          <optgroup label={t('moonpay.regionLatam', { defaultValue: 'Latin America' })}>
            {MOONPAY_HEADLESS_COUNTRIES.map((c) => (
              <option key={c.a2} value={c.a2}>
                {c.name}
              </option>
            ))}
          </optgroup>
          <optgroup label={t('moonpay.regionOther', { defaultValue: 'Other countries' })}>
            {MOONPAY_HOSTED_COUNTRIES.map((c) => (
              <option key={c.a2} value={c.a2}>
                {c.name}
              </option>
            ))}
          </optgroup>
        </select>

        <Box className="rounded-2xl bg-[var(--bg-secondary)] px-4 py-3 dark:bg-[#F8F9FB10]">
          <Typography className="text-xs text-[var(--text-muted)]">
            {headless
              ? t('moonpay.pathHeadless', {
                  defaultValue: 'Local currency {{fiat}}. You’ll verify directly in the app.',
                  fiat,
                })
              : t('moonpay.pathHosted', {
                  defaultValue: 'Local currency {{fiat}}. You’ll finish securely with MoonPay.',
                  fiat,
                })}
          </Typography>
        </Box>

        <Box className="mt-auto">
          <button
            onClick={() => setRouted(true)}
            className="w-full rounded-2xl bg-[var(--primary)] px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90"
          >
            {t('common.continue', { defaultValue: 'Continue' })}
          </button>
        </Box>
      </Box>
    </Box>
  );
};
